/**
 * This file contains authentication parameters. Contents of this file
 * is roughly the same across other MSAL.js libraries. These parameters
 * are used to initialize Angular and MSAL Angular configurations in
 * in app.module.ts file.
 */

import { LogLevel, Configuration, BrowserCacheLocation } from '@azure/msal-browser';
import { environment } from '../environments/environment';

/**
 * Enter here the user flows and custom policies for your B2C application,
 * To learn more about user flows, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  authority: `https://${environment.b2cTenant}.b2clogin.com/${environment.b2cTenant}.onmicrosoft.com/B2C_1_Admin`,
  authorityDomain: `${environment.b2cTenant}.b2clogin.com`,
};

export const msalConfig: Configuration = {
  auth: {
    clientId: environment.b2cClientId, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authority, // Defaults to "https://login.microsoftonline.com/common"
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: '/', // Points to window.location.origin. You must register this URI on Azure portal/App Registration.
    postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: true,
    // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback(logLevel: LogLevel, message: string) {
        // eslint-disable-next-line no-console
        console.log(message);
      },
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */

const BASE_URL =
  environment.partUrlBackendAPI === 'prod'
    ? `https://retailercollective-app.monsterenergy.com`
    : `https://retailercollective-app-${environment.partUrlBackendAPI}.monsterenergy.com`;

export const marketMappingsApi = `${BASE_URL}/api/v1/Admin/market-mappings`;
export const storePointsReportApi = `${BASE_URL}/api/v1/Admin/storepoints-report`;
export const balanceExpirationReportApi = `${BASE_URL}/api/v1/Admin/balance-expiration-report`;
export const storeTransactionsReportApi = `${BASE_URL}/api/v1/Admin/storetransactions-report`;
export const userStoresApi = `${BASE_URL}/api/v1/Admin/user/stores`;
export const userStatusApi = `${BASE_URL}/api/v1/Admin/user/status`;
export const userApi = `${BASE_URL}/api/v1/Admin/user`;
export const auditReportApi = `${BASE_URL}/api/v1/Admin/audit-log-report`;

export const protectedResources = {
  marketMappingsApi: {
    endpoint: marketMappingsApi,
    scopes: environment.scopes,
  },
  storePointsReportApi: {
    endpoint: storePointsReportApi,
    scopes: environment.scopes,
  },
  balanceExpirationReportApi: {
    endpoint: balanceExpirationReportApi,
    scopes: environment.scopes,
  },
  storeTransactionsReportApi: {
    endpoint: storeTransactionsReportApi,
    scopes: environment.scopes,
  },
  auditReportApi: {
    endpoint: auditReportApi,
    scopes: environment.scopes,
  },
  userStoresApi: {
    endpoint: userStoresApi,
    scopes: environment.scopes,
  },
  userStatusApi: {
    endpoint: userStatusApi,
    scopes: environment.scopes,
  },
  userApi: {
    endpoint: userApi,
    scopes: environment.scopes,
  },
};
